/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import AppInput from "../../forms/AppInput";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { updateTutorSession } from "../../../api/endpoints/tutors/sessions";
import moment from "moment-timezone";
import DropDown from "../../forms/dropdown/DropDown";
import SessionModel from "../../../api/models/SessionModel";
import ExistConfirmationModal from "../ExistSessionModal";
import AppButton from "../../buttons/AppButton";
import { makeApiRequest } from "../../../helpers";
import { reportSession } from "../../../api/endpoints/supervisors/sessions";
import { ApiResponse } from "../../../api/apiResponse";

type ReportSessionProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  session: SessionModel | null;
};
export default function ReportSession(props: ReportSessionProps) {
  const { open, setOpen, session } = props;
  const LocalBasePath = "page.tutor.dashboard.room";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const [report, setReport] = useState<string>("");
  const [category, setCategory] = useState<string>("");

  const sendReportSession = () => {
    makeApiRequest(
      () =>
        reportSession(session?.id ?? "", {
          body: report,
          report_category: category,
        }),
      (res: ApiResponse) => {
        if (res.isSuccess) {
          setOpen(false);
          setCategory("");
          setReport("");
        }
      }
    );
  };
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden"
        onClose={setOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div
            data-cy="endSessionModal"
            className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10"
          >
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="pointer-events-auto relative w-screen max-w-2xl">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-500"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-500"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4">
                    <button
                      type="button"
                      className="rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">Close panel</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                  <div className="px-4 sm:px-6">
                    <Dialog.Title className="flex flex-col">
                      {" "}
                      <p className="text-lg font-medium text-gray-900 ">
                        {t("sendReport")}
                      </p>
                      <p className="text-gray-400 text-sm ">
                        {t("descReport")}
                      </p>
                    </Dialog.Title>
                  </div>

                  <div className="relative mt-6 flex-1 px-4 sm:px-6 flex flex-col justify-between h-full">
                    <div>
                      <fieldset>
                        <div className="space-y-5">
                          <div className="relative flex items-start gap-2">
                            <div className="flex h-6 items-center">
                              <input
                                id="comments"
                                aria-describedby="comments-description"
                                name="comments"
                                type="checkbox"
                                className="h-4 w-4 rounded text-brandblue-600 border-gray-300  focus:ring-brandblue-600"
                                onChange={() =>
                                  setCategory("inappropriate_behavior")
                                }
                                checked={category === "inappropriate_behavior"}
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="comments"
                                className="text-sm  text-gray-600"
                              >
                                {t("inappropriateBehavior")}
                              </label>
                            </div>
                          </div>
                          <div className="relative flex items-start gap-2">
                            <div className="flex h-6 items-center">
                              <input
                                id="candidates"
                                aria-describedby="candidates-description"
                                name="candidates"
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-brandblue-600 focus:ring-brandblue-600"
                                onChange={() =>
                                  setCategory("inappropriate_content")
                                }
                                checked={category === "inappropriate_content"}
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="comments"
                                className="text-sm  text-gray-600"
                              >
                                {t("inappropriateContent")}
                              </label>
                            </div>
                          </div>
                          <div className="relative flex items-start gap-2">
                            <div className="flex h-6 items-center">
                              <input
                                id="offers"
                                aria-describedby="offers-description"
                                name="offers"
                                type="checkbox"
                                onChange={() =>
                                  setCategory("inappropriate_language")
                                }
                                checked={category === "inappropriate_language"}
                                className="h-4 w-4 rounded border-gray-300 text-brandblue-600 focus:ring-brandblue-600"
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="comments"
                                className="text-sm  text-gray-600"
                              >
                                {t("inappropriateLanguage")}
                              </label>
                            </div>
                          </div>
                          <div className="relative flex items-start gap-2">
                            <div className="flex h-6 items-center">
                              <input
                                id="offers"
                                aria-describedby="offers-description"
                                name="offers"
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-brandblue-600 focus:ring-brandblue-600"
                                onChange={() => setCategory("others")}
                                checked={category === "others"}
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="comments"
                                className="text-sm  text-gray-600"
                              >
                                {t("others")}
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <textarea
                            rows={4}
                            name="comment"
                            id="comment"
                            className="block w-full rounded-lg border border-gray-200 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset  sm:text-sm sm:leading-6"
                            onChange={(e) => setReport(e.target.value)}
                          />
                        </div>
                      </fieldset>
                      <div className="flex flex-col mt-5 gap-3 sm:flex-row-reverse">
                        <AppButton
                          onClick={() => sendReportSession()}
                          buttonShape="primary-danger"
                        >
                          {t("sendReport")}
                        </AppButton>
                        <AppButton
                          onClick={() => setOpen(false)}
                          buttonShape="secondary"
                        >
                          {t("cancel")}
                        </AppButton>
                      </div>
                    </div>
                    {/* /End replace */}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
